import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { API_BASE_URL, API_ROUTES } from '@core/constants/api.constants';
import { DashboardData, SalesSummary, ServiceSales } from '../models/dashboard.model';

/**
 * Servicio para manejar datos del dashboard administrativo
 * Sigue el mismo patrón que AuthService para consistencia
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  // URLs completas para los endpoints del dashboard
  private readonly dashboardUrl = `${API_BASE_URL}${API_ROUTES.DASHBOARD.BASE}`;
  private readonly salesDailyUrl = `${API_BASE_URL}${API_ROUTES.DASHBOARD.SALES.DAILY}`;
  private readonly salesMonthlyUrl = `${API_BASE_URL}${API_ROUTES.DASHBOARD.SALES.MONTHLY}`;
  private readonly salesYearlyUrl = `${API_BASE_URL}${API_ROUTES.DASHBOARD.SALES.YEARLY}`;
  private readonly servicesUrl = `${API_BASE_URL}${API_ROUTES.DASHBOARD.SERVICES}`;

  constructor(private http: HttpClient) { }

  /**
   * Obtiene los datos completos del dashboard
   * @returns Observable con datos del dashboard tipados
   */
  getDashboardData(): Observable<DashboardData> {
    return this.http.get<DashboardData>(this.dashboardUrl).pipe(
      tap(response => {
        console.log('Dashboard data loaded:', response);
      }),
      catchError(error => {
        console.error('Error al cargar datos del dashboard:', error);
        console.error('Error status:', error.status);
        console.error('Error statusText:', error.statusText);
        console.error('Error headers:', error.headers);
        console.error('Error body:', error.error);
        
        // Si el error tiene detalles específicos, mostrarlos
        if (error.error && error.error.detail) {
          console.error('Error details:', error.error.detail);
          if (Array.isArray(error.error.detail)) {
            error.error.detail.forEach((detail: any, index: number) => {
              console.error(`Detail ${index}:`, detail);
            });
          }
        }
        
        // Si el status es 200 pero Angular lo trata como error,
        // probablemente es un problema de parsing de JSON
        if (error.status === 200) {
          console.error('Status 200 pero tratado como error - posible problema de CORS o parsing JSON');
          console.error('Response text:', error.error);
        }
        
        throw error;
      })
    );
  }

  /**
   * Obtiene las estadísticas de ventas para un período específico
   * @param period Período (daily, monthly, yearly)
   * @returns Observable con estadísticas de ventas tipadas
   */
  getSalesStats(period: 'daily' | 'monthly' | 'yearly'): Observable<SalesSummary> {
    let url: string;
    
    switch (period) {
      case 'daily':
        url = this.salesDailyUrl;
        break;
      case 'monthly':
        url = this.salesMonthlyUrl;
        break;
      case 'yearly':
        url = this.salesYearlyUrl;
        break;
      default:
        throw new Error(`Período inválido: ${period}`);
    }
    
    return this.http.get<SalesSummary>(url).pipe(
      tap(response => {
        }),
      catchError(error => {
        console.error(`Error al cargar estadísticas de ventas ${period}:`, error);
        throw error;
      })
    );
  }

  /**
   * Obtiene las estadísticas de servicios
   * @returns Observable con estadísticas de servicios tipadas
   */
  getServiceStats(): Observable<ServiceSales[]> {
    return this.http.get<ServiceSales[]>(this.servicesUrl).pipe(
      tap(response => {
        }),
      catchError(error => {
        console.error('Error al cargar estadísticas de servicios:', error);
        throw error;
      })
    );
  }
}
